import * as React from 'react'
import { AnyAction } from 'redux'
import { useTranslation } from 'react-i18next'

import { ChannelsForm, LayersForm, MultiMediaForm, PlaylistsForm, SinglePromoForm } from '../Forms'
import * as ContentActions from '../../../actions/content'
import { ACL } from '../../../constants'
import { ArrayUtils, EstateUtils } from '../../../utils'
import { Types as LayersTypes } from '../../../actions/content/layers'
import { Types as MediaTypes } from '../../../actions/content/media'
import { Column, Modal, Padding, SearchInput, Table } from '../../../components'
import { PlaylistsForm as PlaylistsFormExtra } from '../FormsEdit'
import * as DeviceActions from '../../../actions/device'
import * as NotificationActions from '../../../actions/notifications'
import { useSize } from '../../../components/utils'
import { Header, HeaderBar, Inline, SearchBoxContainer } from '../../../components/Screens/Content/Content/Content.style'
import ContentPlaylist from '../../../components/Screens/Content/Content/ContentPlaylist'
import ContentMediaLayers from '../../../components/Screens/Content/Content/ContentMediaLayers'
import { useAppSelector } from '../../../reducers'
import { useAppDispatch } from '../../../actions/types'
import { Sizes as PaddingSizes } from '../../../components/Padding'
import type { Media, Layer } from '../../../types'
import ChannelsExpander from './ChannelsExpander'
import type { ExtendedChannel, ExtendedLayer, ExtendedMedia, ExtendedPlaylist, Pagination } from './types'
import {
    useContentURLParams,
    useChannels,
    useSelectedChannel,
} from './hooks'
import channelsTableDisplay from './channels.table'
import { usePageTitle } from '../../../actions/app'
import ChannelsSubBar from './ChannelsSubBar'
import { useRequiredDataFetch } from './hooks'
import useChannelsFiltersParams from './ChannelsSubBar/ChannelsFilters/useChannelsFiltersParams'

/**
 * Content Page Root Container
 * @returns 
 */
export default function Content() {
    /**
     * Misc hooks
     */
    const dispatch = useAppDispatch()
    const { isMobile } = useSize()
    const { t } = useTranslation()
    useRequiredDataFetch()
    const [channelFilterParams] = useChannelsFiltersParams()

    /**
     * Selectors
     */
    const channels = useChannels()
    const playlists = useAppSelector<ExtendedPlaylist[]>(state => state.content.playlists)
    const layers = useAppSelector<ExtendedLayer[]>(state => state.content.layers)
    const media = useAppSelector<ExtendedMedia[]>(state => state.content.media)
    const channelPagination = useAppSelector<Pagination>(state => state.content.pagination.channels)
    const isWorkingChannels = useAppSelector<boolean>(state => state.content.isWorking.channels)
    const isWorkingPlaylists = useAppSelector<boolean>(state => state.content.isWorking.channelSingle)
    const isWorkingLayers = useAppSelector<boolean>(state => state.content.isWorking.playlists)
    const isWorkingMedia = useAppSelector<boolean>(state => state.content.isWorking.playlists)
    const selectedChannel = useSelectedChannel()
    const selectedPlaylist = useAppSelector<ExtendedPlaylist | null>(
        state => state.content.selected.playlists,
    )
        
    const hasPermissionToApproveLayers = useAppSelector(
        state => EstateUtils.hasPermission(state, { acls: [ACL.APPROVE_LAYERS] })
    )

    /**
     * URL query params
     */
    const {
        navigateInContentPage,
        searchChannel,
        selectedChannelId,
        selectedPlaylistId,
    } = useContentURLParams()

    /**
     * Page title
     */
    usePageTitle(selectedChannel?.name ?? t('Content'))

    /**
     * Variables definitions
     */
    const selectedChannelsCounter = channels.filter((channel) => channel.selected).length
    const channelsMediaSize = channels.reduce((carry, channel) => {
        const { selected, channel_size } = channel

        if (selected) {
            return carry + (channel_size ?? 0)
        }

        return carry
    }, 0)
    const optionsPlaylists = React.useMemo(() => {
        if (!selectedChannel) {
            return []
        }

        const { playlists: channelPlaylists = [] } = selectedChannel
        const selectedIds = channelPlaylists.map(({ id }) => id)

        return playlists.filter(({ id }) => !selectedIds.includes(id)).map(({ id, name }) => ({ key: id, value: name }))
    }, [playlists, selectedChannel])
    const optionsLayers = React.useMemo(() => {
        if (!selectedPlaylist) {
            return []
        }

        const { layers: playlistLayers } = selectedPlaylist
        if (playlistLayers) {
            const selectedIds = playlistLayers.map(({ id }) => id)

            return layers.filter(({ id }) => !selectedIds.includes(id)).map(({ id, name }) => ({ key: id, value: name }))
        }
        return []
    }, [layers, selectedPlaylist])
    const optionsMedia = React.useMemo(() => {
        return media.map(({ id, name }) => ({ key: id, value: name }))
    }, [media])

    /**
     * State definitions
     */
    const [
        childrenExpanderChannelsHeight,
        setChildrenExpanderChannelsHeight,
    ] = React.useState(0)
    const [isFormChannelsOpen, setIsFormChannelsOpen] = React.useState(false)
    const [isFormPlaylistsOpen, setIsFormPlaylistsOpen] = React.useState(false)
    const [isFormLayersOpen, setIsFormLayersOpen] = React.useState(false)
    const [isFormMediaOpen, setIsFormMediaOpen] = React.useState(false)
    const [isFormSinglePromoOpen, setIsFormSinglePromoOpen] = React.useState(false)
    const [textPlaylist, setTextPlaylist] = React.useState('')
    const [textLayers, setTextLayers] = React.useState('')
    const [textMedia, setTextMedia] = React.useState('')
    const [isVideoPlayerOpen, setIsVideoPlayerOpen] = React.useState(false)
    const [videoPlayerUrl, setVideoPlayerUrl] = React.useState<string>()

    /**
     * Bulk select a channel
     * @param e 
     * @param id 
     */
    const bulkSelectionChannels = (e: React.MouseEvent, id: number): void => {
        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        const indexChannel = channels.findIndex((channel) => channel.id === id)

        if (indexChannel > -1) {
            const { selected } = channels[indexChannel]
            const newChannelItem = {
                ...channels[indexChannel],
                selected: !selected,
            }
            const newList = ArrayUtils.insertItem(channels, indexChannel, newChannelItem)

            dispatch(ContentActions.changeChannelsArray(newList))
        }
    }

    const bulkSelectionPlaylists = (e: React.MouseEvent, id: number): void => {
        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        if (!selectedChannel) {
            return
        }

        const { playlists } = selectedChannel
        const indexPlaylist = playlists.findIndex((channel) => channel.id === id)

        if (indexPlaylist > -1) {
            const { selected } = playlists[indexPlaylist] as ExtendedPlaylist
            const newPlaylistItem = {
                ...playlists[indexPlaylist],
                selected: !selected,
            }
            const newList = ArrayUtils.insertItem(playlists, indexPlaylist, newPlaylistItem)
            dispatch(ContentActions.changePlaylistsArray(newList))
        }
    }

    const bulkSelectionMedia = (index: number) => {
        const { media } = selectedPlaylist || { media: undefined}

        if (media && media.length > index) {
            const { selected } = media[index]
            const newMediaItem = {
                ...media[index],
                selected: !selected,
            }
            const newList = ArrayUtils.insertItem(media, index, newMediaItem)
            dispatch(ContentActions.changeSelectedMediaArray(newList))
        }
    }

    /**
     * Function for fetching the next page of the channel list
     */
    const nextPageFunc = React.useCallback(() => {
        const { currentPage } = channelPagination
        dispatch(
            ContentActions.fetchChannels(
                currentPage + 1,
                { chains: channelFilterParams.chains, name: searchChannel },
            )
        )
    }, [channelFilterParams.chains, channelPagination, dispatch, searchChannel])

    /**
     * Serve the next page function only if it's required
     * @returns 
     */
    const buildNextPageFunc = () => {
        const { to, total } = channelPagination

        if (!isWorkingPlaylists && to < total) {
            return nextPageFunc
        }

        return null
    }

    const onChangeChannel = (channel: number, search?: string): void => {
        dispatch(ContentActions.deselectChannel())
        dispatch(ContentActions.deselectPlaylist())
        navigateInContentPage({ channel, search })
    }
    
    const onChangePlaylist = (playlist: number): void => {
        navigateInContentPage({ channel: selectedChannelId, playlist })
    }

    /**
     * Attaches a layer to a playlist
     * @param key Layer id to be added
     */
    const onChangeLayers = async (key: number): Promise<void> =>  {
        if (!selectedPlaylist) {
            return 
        }

        const { id: playlistID } = selectedPlaylist
        const layerToAdd = layers.find(({ id }) => id === key)

        if (!layerToAdd) {
            return
        }

        const newLayersList = [layerToAdd]

        await dispatch(ContentActions.updatePlaylist(playlistID, {
            ...selectedPlaylist,
            layers: newLayersList,
        }))
    }

    /**
     * Attaches a media to a playlist
     * @param key Media id to be added
     */
    const onChangeMedia = async (key: number): Promise<void> => {
        if (!selectedPlaylist) {
            return 
        }

        const { id: playlistID, media: mediaList } = selectedPlaylist
        const mediaToAdd = media.find(({ id }) => id === key)

        if (!mediaToAdd) {
            return
        }

        const newMediaList = [...mediaList, mediaToAdd]

        await dispatch(ContentActions.updatePlaylist(playlistID, {
            ...selectedPlaylist,
            media: newMediaList,
        }))
    }

    /**
     * Once all the new channel has been stored, we attach it to the playlist
     * @param storedAction Redux action from upload
     * @async
     */
    const onFinishSubmitChannels = async (storedAction: AnyAction): Promise<void> => {
        const {
            payload: { id: channelID, name: searchChannel },
        } = storedAction
        setIsFormChannelsOpen(false)
        onChangeChannel(channelID, searchChannel)
    }

    /**
     * Once all the layers have been uploaded to the server, and created the record,
     * we attach them to the playlist
     * @param storedLayersList Redux action from upload
     * @param assignToAllPlaylists If true, the layer gets assigned to all playlists in this channel.
     * If false, it's only assigned to the selected playlist
     * @async
     */
    const onFinishSubmitLayers = async (storedLayersList: Layer[], assignToAllPlaylists = false): Promise<void> => {
        if (!selectedPlaylist || !selectedChannel) {
            return
        }

        // Append stored item to playlists' array
        const newLayerlist = [...storedLayersList]

        if (assignToAllPlaylists) {
            const { playlists } = selectedChannel

            await Promise.all(playlists.map(async playlist => {
                const { id: playlistID } = playlist

                await dispatch(ContentActions.updatePlaylist(playlistID, {
                    ...playlist,
                    layers: newLayerlist,
                }))
            }))

        } else {
            const { id: playlistID } = selectedPlaylist
    
            await dispatch(ContentActions.updatePlaylist(playlistID, {
                ...selectedPlaylist,
                layers: newLayerlist,
            }))
        }

        setIsFormLayersOpen(false)
    }

    /**
     * Method to be executed when created a new playlist
     */
    const onFinishSubmitPlaylists = async (storedAction: AnyAction): Promise<void> => {
        const {
            payload: { id: playlistID },
        } = storedAction

        if (!playlistID || !selectedChannel) {
            return
        }

        const { id: channelID, playlists } = selectedChannel

        await dispatch(ContentActions.updateChannel(channelID, {
            ...selectedChannel,
            playlists: [...playlists, { id: playlistID }],
        }))
        setIsFormPlaylistsOpen(false)
    }
        
    const onChangeSearch = ({ target: { value } }: { target: { value: string } }): void => {
        navigateInContentPage({
            channel: selectedChannelId,
            playlist: selectedPlaylistId,
            search: value,
        })
    }

    const onChangePlaylistInput = async (key: number): Promise<void> => {
        if (!selectedChannel) {
            return
        }

        const { id: channelID, playlists: playlistsList } = selectedChannel
        const playlistsToAdd = playlists.find(({ id }) => id === key)

        if (!playlistsToAdd) {
            return
        }

        const newPlaylistsList = [...playlistsList, playlistsToAdd]

        await dispatch(
            ContentActions.updateChannel(channelID, {
                ...selectedChannel,
                playlists: newPlaylistsList,
            })
        )
    }

    /**
     * Fetches new suggestions for the layers input
     * @param name Name written on the input
     */
    const onTextChangeLayers = (name: string): void => {
        if (name !== textLayers) {
            dispatch(ContentActions.fetchLayers(null, { name }))
            setTextLayers(name)
        }
    }

    /**
     * Fetches new suggestions for the media input
     * @param {string} name Name written on the input
     */
    const onTextChangeMedia = (name: string): void => {
        if (name !== textMedia) {
            dispatch(ContentActions.fetchMedia(undefined, { name }))
            setTextMedia(name)
        }
    }


    /**
     * Fetches new suggestions for the playlists input
     * @param name Name written on the input
     */
    const onTextChangePlaylists = (name: string): void => {
        if (name !== textPlaylist) {
            const chains = channelFilterParams.chains
            dispatch(ContentActions.fetchPlaylists(undefined, { chains, name }))
            setTextPlaylist(name)
        }
    }

    const onRemoveChannels = (): void => {
        const channelsToDelete = channels.filter((channel) => channel.selected)
        const channelsIds = channelsToDelete.map((channel) => channel.id)

        channelsToDelete.forEach((channel) => {
            dispatch(ContentActions.deleteChannel(channel.id))
        })

        // If deleting the selected channel, redirect to the root page
        if (channelsIds.includes(selectedChannelId)) {
            navigateInContentPage({ search: searchChannel })
        }
    }

    const onRemovePlaylists = async (toDelete: number | ExtendedPlaylist[]) => {
        if (!selectedChannel) {
            return
        }

        // const { selectedChannel, updateChannel } = this.props
        const { id: channelID, playlists: playlistsList } = selectedChannel
        let newPlaylistslist = playlistsList

        if (typeof toDelete === 'number') {
            // Clone the list without the removed element
            newPlaylistslist = [
                ...playlistsList.slice(0, toDelete),
                ...playlistsList.slice(toDelete + 1, playlistsList.length),
            ]
        } else if (typeof toDelete === 'object' && Array.isArray(toDelete)) {
            newPlaylistslist = toDelete.filter((playlist) => !playlist.selected)
        }

        await dispatch(
            ContentActions.updateChannel(channelID, {
                ...selectedChannel,
                playlists: newPlaylistslist,
            })
        )
    }

    const onRemoveLayers = async (index: number): Promise<void> => {
        if (!selectedPlaylist) {
            return
        }

        const { id: playlistID, layers: layerList } = selectedPlaylist

        // Clone the list without the removed element
        const newLayerslist = [...layerList.slice(0, index), ...layerList.slice(index + 1, layerList.length)]

        await dispatch(ContentActions.updatePlaylist(playlistID, {
            ...selectedPlaylist,
            layers: newLayerslist,
        }))
    }

    const onRemoveMedia = async (mediaID: number, orderId: number): Promise<void> => {
        if (!selectedPlaylist) {
            return
        }

        const { id: playlistID, media: mediaList } = selectedPlaylist

        if (typeof mediaID === 'number') {
            // Clone the list without the removed element
            const newMedialist = mediaList.filter(({ id, order }) => !(id === mediaID && order === orderId))

            await dispatch(ContentActions.updatePlaylist(playlistID, {
                ...selectedPlaylist,
                media: newMedialist,
            }))
        } else {
            const newMediaList = mediaList.filter((m) => !m.selected)
            await dispatch(ContentActions.updatePlaylist(playlistID, {
                ...selectedPlaylist,
                media: newMediaList,
            }))
        }
    }

    const onClickPlaylist = (key: number): void => {
        if (typeof key !== 'number') {
            return
        }

        onChangePlaylist(key)
    }

    /**
     * On open media form
     * @param layerID
     */
    const onApproveLayer = async (layerID: number): Promise<void> => {
        await dispatch(ContentActions.approveLayer(layerID, true, true))
    }

    /**
     * On open media form
     * @param mediaID
     */
    const onApproveMedia = async (mediaID: number) => {
        await dispatch(
            ContentActions.approveMedia(
                mediaID,
                true,
                { inSelectedPlaylist: true },
            ),
        )
    }

    const onDownloadLayer = async (index: number): Promise<void> => {
        if (!selectedPlaylist) {
            return
        }

        const { layers } = selectedPlaylist

        if (layers.length > index) {
            const selectedLayer = layers[index]

            const response = await dispatch(
                ContentActions.downloadLayer(selectedLayer.id)
            )

            if (response.type === LayersTypes.DOWNLOAD_LAYER_SUCCESS) {
                const downloadTag = document.createElement('a')
                downloadTag.href = response.payload
                downloadTag.download = selectedLayer.name
                downloadTag.click()
            }
        }
    }

    /**
     * On download media button being clicked
     * @param {*} mediaID ID of the media to download
     * @param {*} videoPlayer If true, it plays the video inline. If false, it
     * will download it to the system
     */
    const onDownloadMedia = async (mediaID: number, videoPlayer = false): Promise<void> => {
        if (!selectedPlaylist) {
            return
        }

        const { media } = selectedPlaylist

        const selectedMedia = media.find(({ id }) => id === mediaID)

        const response = await dispatch(ContentActions.downloadMedia(mediaID, videoPlayer))

        if (response.type !== MediaTypes.DOWNLOAD_MEDIA_SUCCESS || !selectedMedia) {
            return
        }

        const { payload: url } = response

        // If videoPlayer false, download it
        if (!videoPlayer) {
            const downloadTag = document.createElement('a')
            downloadTag.href = url
            downloadTag.target = '_blank'
            downloadTag.download = selectedMedia.name
            downloadTag.click()
            return
        }

        setIsVideoPlayerOpen(true)
        setVideoPlayerUrl(url)
    }

    /**
     * On drag and drop media
     * @param {Object} media
     */
    const onDropMedia = async (media: Media[]): Promise<void> => {
        if (!selectedPlaylist) {
            return
        }

        const { id: playlistID } = selectedPlaylist

        await dispatch(ContentActions.updatePlaylist(playlistID, {
            ...selectedPlaylist,
            media,
        }))
    }

    const resetSelection = () => {
        const resetedChannels = channels.map((channel) => {
            return { ...channel, selected: false }
        })
        dispatch(ContentActions.changeChannelsArray(resetedChannels))
    }

    const onCloseFormSinglePromo = () => {
        dispatch(ContentActions.resetForm())
        resetSelection()
        setIsFormSinglePromoOpen(false)
    }

    const restart = async (uid: string) => {
        const response = await dispatch(DeviceActions.reloadPromosViaAPI(uid))

        if (response.type === 'API_PROMO_RELOAD_FAILURE') {
            dispatch(
                NotificationActions.notify({
                    title: 'Error',
                    body: `Update device ${uid} with new promos failed`,
                }),
            )
        } else if (response.type === 'API_PROMO_RELOAD_SUCCESS') {
            dispatch(
                NotificationActions.notify({
                    title: 'Succeed',
                    body: `Update device ${uid} with new promos succeed `,
                }),
            )
        }
    }

    const singlePromoFormFetchMedia = (
        page: number,
        filters: {
            name: string;
            pendingApproval: boolean;
            pendingPush: boolean;
            pendingTest: boolean;
            tag: string;
        }
    ) => {
        dispatch(ContentActions.fetchMedia(page, filters))
    }

    const { name: channelName } = selectedChannel || {}

    return (
        <Column width="100%" style={{ overflow: 'hidden' }}>
            <Column style={{ overflow: 'hidden' }} width={isMobile ? '100%' : '400px'}>
                <Table
                    childrenExpanderHeight={childrenExpanderChannelsHeight}
                    complexTable
                    currentPage={channelPagination.currentPage}
                    data={channels}
                    display={channelsTableDisplay({ bulkSelectionChannels, t })}
                    expander={(isSelected: boolean, channel: ExtendedChannel) => {
                        if (!selectedChannelId || channel?.id !== selectedChannelId) {
                            return null
                        }

                        return (
                            <ChannelsExpander
                                setChildrenExpanderChannelsHeight={setChildrenExpanderChannelsHeight}
                                isOpen={isSelected}
                            />
                        )
                    }}
                    isLoading={isWorkingChannels}
                    keyField="id"
                    nextPage={buildNextPageFunc()}
                    onClickRow={onChangeChannel}
                    selectedItem={selectedChannelId}
                    sortable={['name']}
                    hideDeviceButtons
                >
                    <>
                        <Header>
                            <Column width="40%" notFullHeight>
                                <div className="page-title">{t('Channels')}</div>
                            </Column>
                        </Header>
                        <HeaderBar>
                            <Padding left size={PaddingSizes.sm}>
                                    <Padding right inline style={{ width: '50%' }}>
                                        {!isWorkingChannels && t('filter-counter', {
                                            filtered: channels.length,
                                            total: channelPagination.total,
                                        })}
                                    </Padding>
                                    <Inline style={{ textAlign: 'right', width: '50%' }}>
                                        <SearchBoxContainer>
                                            <SearchInput
                                                fullwidth
                                                onChange={onChangeSearch}
                                                value={searchChannel}
                                            />
                                        </SearchBoxContainer>
                                    </Inline>
                            </Padding>
                        </HeaderBar>
                        <Padding size={PaddingSizes.xs}>
                            <ChannelsSubBar
                                channelsMediaSize={channelsMediaSize}
                                onOpenChannelForm={() => setIsFormChannelsOpen(true)}
                                onRemoveChannels={onRemoveChannels}
                                selectedChannelsCounter={selectedChannelsCounter}
                            />
                        </Padding>
                    </>
                </Table>
            </Column>
            <MultiMediaForm
                isOpen={isFormMediaOpen}
                onClickCancel={() => setIsFormMediaOpen(false)}
                // onFinishSubmit={fetchPlaylistsData}
                onFinishSubmit={() => null}
            />
            <LayersForm
                isOpen={isFormLayersOpen}
                onClickCancel={() => setIsFormLayersOpen(false)}
                onFinishSubmit={onFinishSubmitLayers}
            />
            <ChannelsForm
                isOpen={isFormChannelsOpen}
                onClickCancel={() => setIsFormChannelsOpen(false)}
                onFinishSubmit={onFinishSubmitChannels}
            />
            <PlaylistsForm
                isOpen={isFormPlaylistsOpen}
                onClickCancel={() => setIsFormPlaylistsOpen(false)}
                onFinishSubmit={onFinishSubmitPlaylists}
                selectedChainId={selectedChannel?.chain_id}
            />
            <Modal
                isOpen={isVideoPlayerOpen}
                onClose={() => setIsVideoPlayerOpen(false)}
                minWidth="50%"
            >
                <Padding size={PaddingSizes.ssm}>
                    <video controls autoPlay width="auto" style={{ height: isMobile ? 'auto' : '60vh', maxWidth: '85vw' }}>
                        <track kind="captions" />
                        <source src={videoPlayerUrl} />
                    </video>
                </Padding>
            </Modal>
            <SinglePromoForm
                isOpen={isFormSinglePromoOpen}
                onClickCancel={onCloseFormSinglePromo}
                onFinishSubmit={onFinishSubmitPlaylists}
                fetchMedia={singlePromoFormFetchMedia}
                onClickSubmit={onCloseFormSinglePromo}
                reloadPromo={restart}
            />
            <ContentPlaylist
                bulkSelectionPlaylists={bulkSelectionPlaylists}
                channelName={channelName}
                expander={(isOpen: boolean, playlist: ExtendedPlaylist) => (
                    <PlaylistsFormExtra
                        isOpen={isOpen}
                        playlist={playlist}
                        selectedChainId={selectedChannel?.chain_id}
                    />
                )}
                isWorking={isWorkingPlaylists}
                onChangePlaylistInput={onChangePlaylistInput}
                onClickPlaylist={onClickPlaylist}
                onOpenFormPlaylists={() => setIsFormPlaylistsOpen(true)}
                onRemovePlaylists={onRemovePlaylists}
                onTextChangePlaylists={onTextChangePlaylists}
                optionsPlaylists={optionsPlaylists}
                playlists={selectedChannel?.playlists}
                selectedPlaylist={selectedPlaylistId}
            />
            <ContentMediaLayers
                bulkSelection={bulkSelectionMedia}
                channelName={channelName}
                hasPermissionToApproveLayers={hasPermissionToApproveLayers}
                isWorkingLayers={isWorkingLayers}
                isWorkingMedia={isWorkingMedia}
                onApproveLayer={onApproveLayer}
                onApproveMedia={onApproveMedia}
                onChangeLayers={onChangeLayers}
                onChangeMedia={onChangeMedia}
                onDownloadLayer={onDownloadLayer}
                onDownloadMedia={onDownloadMedia}
                onDropMedia={onDropMedia}
                onOpenFormLayers={() => setIsFormLayersOpen(true)}
                onOpenFormMedia={() => setIsFormMediaOpen(true)}
                onRemoveLayers={onRemoveLayers}
                onRemoveMedia={onRemoveMedia}
                onTextChangeLayers={onTextChangeLayers}
                onTextChangeMedia={onTextChangeMedia}
                optionsLayers={optionsLayers}
                optionsMedia={optionsMedia}
                selectedPlaylist={selectedPlaylist}
            />
        </Column>
    )
}
